import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  TableCell,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../../components/CustomTable";
import { StyledTableCell } from "../../components/styled";
import {
  createLeague,
  deleteLeague,
  getLeagues,
  updateLeague,
} from "../../services/league.service";
import {
  normalizeFieldErrors,
  validatePngFile,
  useImageUploadField,
} from "../../utils";

const ICON_COL_WIDTH_PX = 120;
const ICON_SIZE_PX = 32;

const LeagueIcon = ({ name, icon }) => {
  const [failed, setFailed] = useState(false);

  if (!icon || failed) {
    return (
      <Box
        sx={{
          width: ICON_SIZE_PX,
          height: ICON_SIZE_PX,
          borderRadius: 1,
          bgcolor: "action.hover",
        }}
      />
    );
  }

  return (
    <Link
      href={icon}
      target="_blank"
      rel="noreferrer"
      aria-label={name ? `${name} icon` : "League icon"}
    >
      <img
        src={icon}
        alt=""
        onError={() => setFailed(true)}
        style={{
          display: "block",
          width: ICON_SIZE_PX,
          height: ICON_SIZE_PX,
          objectFit: "contain",
        }}
        loading="lazy"
      />
    </Link>
  );
};

const Leagues = () => {
  const queryClient = useQueryClient();
  const [sortOrder, setSortOrder] = useState("ASC");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit
  const [editingLeague, setEditingLeague] = useState(null);

  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const imageField = useImageUploadField({
    mode,
    currentUrl: editingLeague?.icon,
  });

  const resetForm = () => {
    setName("");
    setFormError("");
    setFieldErrors({});
    imageField.reset();
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingLeague(null);
    resetForm();
  };

  const openCreate = () => {
    setMode("create");
    setEditingLeague(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (league) => {
    setMode("edit");
    setEditingLeague(league);
    setName(league?.name ?? "");
    setFormError("");
    setFieldErrors({});
    imageField.reset();
    setOpen(true);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
  };

  const listQueryKey = useMemo(() => ["leagues"], []);

  const { mutate: createNewLeague, isPending: isCreatePending } = useMutation({
    mutationKey: ["createLeague"],
    mutationFn: (payload) => createLeague(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(listQueryKey);
      closeDialog();
    },
    onError: (err) => {
      const nextFieldErrors = normalizeFieldErrors(err?.fieldErrors);
      setFieldErrors(nextFieldErrors);
      setFormError(err?.message || "Failed to create league");
    },
  });

  const { mutate: updateExistingLeague, isPending: isUpdatePending } =
    useMutation({
      mutationKey: ["updateLeague"],
      mutationFn: ({ id, payload }) => updateLeague(id, payload),
      onSuccess: () => {
        queryClient.invalidateQueries(listQueryKey);
        closeDialog();
      },
      onError: (err) => {
        const nextFieldErrors = normalizeFieldErrors(err?.fieldErrors);
        setFieldErrors(nextFieldErrors);
        setFormError(err?.message || "Failed to update league");
      },
    });

  const { mutate: removeLeague, isPending: isDeletePending } = useMutation({
    mutationKey: ["deleteLeague"],
    mutationFn: (id) => deleteLeague(id),
    onSuccess: () => {
      queryClient.invalidateQueries(listQueryKey);
    },
    onError: (err) => {
      alert(err?.message || "Failed to delete league");
    },
  });

  const isSaving = isCreatePending || isUpdatePending;

  const submit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setFieldErrors({ name: "Name is required" });
      return;
    }

    const nextFileError = validatePngFile(imageField.file);
    if (nextFileError) {
      imageField.setFileError(nextFileError);
      return;
    }

    setFormError("");
    setFieldErrors({});

    const payload = {
      name: trimmedName,
      file: imageField.file || undefined,
      removeIcon: !imageField.file && imageField.isRemoved ? true : undefined,
    };

    if (
      mode === "edit" &&
      editingLeague?.id !== null &&
      editingLeague?.id !== undefined
    ) {
      updateExistingLeague({ id: editingLeague.id, payload });
      return;
    }

    createNewLeague(payload);
  };

  const canShowRemoveImage = imageField.canShowRemove;

  const renderHeader = () => (
    <TableRow>
      <StyledTableCell
        sx={{
          width: ICON_COL_WIDTH_PX,
          minWidth: ICON_COL_WIDTH_PX,
          maxWidth: ICON_COL_WIDTH_PX,
        }}
      >
        Icon
      </StyledTableCell>

      <StyledTableCell>
        <TableSortLabel
          active
          direction={sortOrder === "ASC" ? "asc" : "desc"}
          onClick={toggleSortOrder}
        >
          Name
        </TableSortLabel>
      </StyledTableCell>

      <StyledTableCell>Actions</StyledTableCell>
    </TableRow>
  );

  const onDeleteClick = (league) => {
    if (
      window.confirm(
        `Delete league "${league?.name ?? league?.id}"? This action cannot be undone.`,
      )
    ) {
      removeLeague(league.id);
    }
  };

  const renderRow = (league) => (
    <TableRow key={league.id}>
      <TableCell
        sx={{
          width: ICON_COL_WIDTH_PX,
          minWidth: ICON_COL_WIDTH_PX,
          maxWidth: ICON_COL_WIDTH_PX,
        }}
      >
        <LeagueIcon name={league?.name} icon={league?.icon} />
      </TableCell>

      <TableCell>{league.name}</TableCell>

      <TableCell>
        <IconButton aria-label="edit" onClick={() => openEdit(league)}>
          <EditIcon />
        </IconButton>

        <IconButton
          aria-label="delete"
          color="error"
          onClick={() => onDeleteClick(league)}
          disabled={isDeletePending}
        >
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  const nameHelper = fieldErrors?.name || " ";

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Create league
        </Button>
      </Box>

      <CustomTable
        name="leagues"
        getItemsMethod={getLeagues}
        renderHeader={renderHeader}
        renderRow={renderRow}
        columnCount={3}
        queryKeyParts={[sortOrder]}
        queryParams={{ sortOrder }}
        pageResetKey={sortOrder}
      />

      <Dialog
        open={open}
        onClose={() => !isSaving && closeDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {mode === "edit" ? "Edit league" : "Create league"}
        </DialogTitle>

        <DialogContent>
          {formError ? (
            <Typography sx={{ mt: 1 }} color="error">
              {formError}
            </Typography>
          ) : null}

          <TextField
            fullWidth
            required
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fieldErrors?.name) {
                setFieldErrors((prev) => ({ ...prev, name: undefined }));
              }
            }}
            error={Boolean(fieldErrors?.name)}
            helperText={nameHelper}
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {mode === "edit" ? "Replace icon (optional)" : "Icon (optional)"}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button variant="outlined" component="label" disabled={isSaving}>
                Choose PNG
                <input
                  hidden
                  type="file"
                  accept="image/png"
                  ref={imageField.fileInputRef}
                  onChange={(e) => {
                    const next = e.target.files?.[0] || null;
                    imageField.onPickFile(next);
                  }}
                />
              </Button>

              {imageField.previewUrl ? (
                <img
                  src={imageField.previewUrl}
                  alt=""
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: "contain",
                    display: "block",
                    borderRadius: 6,
                    background: "#f0f0f0",
                  }}
                />
              ) : mode === "edit" ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current:
                  </Typography>
                  {imageField.isRemoved ? (
                    <Typography variant="body2" color="text.secondary">
                      Save to remove icon
                    </Typography>
                  ) : (
                    <LeagueIcon
                      name={editingLeague?.name}
                      icon={editingLeague?.icon}
                    />
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No file selected
                </Typography>
              )}

              {imageField.file ? (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    imageField.clearSelectedFile();
                  }}
                  disabled={isSaving}
                >
                  Clear
                </Button>
              ) : null}

              {canShowRemoveImage ? (
                <Button
                  variant="text"
                  color="error"
                  disabled={isSaving}
                  onClick={() => {
                    imageField.removeCurrent();
                  }}
                >
                  Remove icon
                </Button>
              ) : null}
            </Box>

            {imageField.fileError ? (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                {imageField.fileError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} disabled={isSaving}>
            Cancel
          </Button>

          <Button
            onClick={submit}
            variant="contained"
            disabled={isSaving || Boolean(imageField.fileError)}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Leagues;
